import zoomLogoText from '@src/assets/platform-logos/zoom-logotext.svg';
import { useDatoPerson, useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

const ZoomAuthButton = ({
  onAuthSuccess,
}: {
  onAuthSuccess?: (tokens: any) => void;
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [zoomEmail, setZoomEmail] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [checkTrigger, setCheckTrigger] = useState(0);
  const { userIdentityStore } = useStore();
  const videoCreatorStore = useVideoCreatorStore();
  const datoPerson = useDatoPerson();
  const [searchParams] = useSearchParams();

  // Force a re-check when component mounts or focus returns to window
  useEffect(() => {
    const handleFocus = () => {
      setCheckTrigger(prev => prev + 1);
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => {
    // Check URL params for OAuth callback
    const params = new URLSearchParams(window.location.search);
    const authStatus = params.get('zoom_auth');

    if (authStatus === 'success') {
      const email = params.get('email');
      setIsAuthenticated(true);
      setZoomEmail(email || 'Connected');

      // Force a re-check after successful OAuth
      setCheckTrigger(prev => prev + 1);

      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);

      // Notify parent component
      if (onAuthSuccess) {
        onAuthSuccess({ email });
      }
    } else if (authStatus === 'error') {
      const message = params.get('message');
      console.error('Zoom auth error:', message);
      // Show error to user
    }

    // Check authentication in order of preference:
    // 1. First check datoPerson if available (persistent storage)
    // 2. Then check API/memory storage (temporary storage)

    // Add a small delay after successful OAuth to allow person data to refresh
    if (authStatus === 'success') {
      setTimeout(() => {
        console.log('datoPerson', datoPerson);
        if (datoPerson?.person) {
          // Person record exists
          if (datoPerson.hasZoomInfo() && !datoPerson.isZoomTokenExpired()) {
            const zoomInfo = datoPerson.getZoomInfo();
            setIsAuthenticated(true);
            setZoomEmail(zoomInfo?.email || 'Connected');
            return;
          }
        }
        // Fallback to API check
        checkExistingAuth();
      }, 1000); // Wait 1 second for person data to refresh
    } else {
      console.log('datoPerson 2', datoPerson);
      if (datoPerson?.person) {
        // Person record exists
        if (datoPerson.hasZoomInfo() && !datoPerson.isZoomTokenExpired()) {
          const zoomInfo = datoPerson.getZoomInfo();
          setIsAuthenticated(true);
          setZoomEmail(zoomInfo?.email || 'Connected');
          return; // Don't need to check API if we have valid datoPerson info
        }
      }

      // Always check API as fallback (for users without Person records or expired tokens)
      checkExistingAuth();
    }
  }, [datoPerson, datoPerson?.person?.zoomInfo, onAuthSuccess, checkTrigger]);

  const checkExistingAuth = async () => {
    console.log('Checking existing auth...', {
      personId: datoPerson?.person?.id,
      hasZoomInfo: datoPerson?.hasZoomInfo(),
      zoomInfo: datoPerson?.getZoomInfo(),
    });

    try {
      // First try to get zoom info from Person record
      const personId = datoPerson?.person?.id;
      const identity = userIdentityStore.currentIdentity;
      // Identity.email now always has a value (placeholder for Dato users)
      const email = identity?.email || datoPerson?.person?.email;

      if (personId || email) {
        const params = new URLSearchParams();
        if (personId) params.append('personId', personId);
        if (email) params.append('email', email);

        const apiUrl = process.env.REACT_APP_API_URL || '';
        const zoomInfoResponse = await fetch(
          `${apiUrl}/api/zoom/get-zoom-info?${params.toString()}`,
        );

        if (zoomInfoResponse.ok) {
          const data = await zoomInfoResponse.json();
          if (data.authenticated) {
            setIsAuthenticated(true);
            setZoomEmail(data.email);
            return; // Found valid auth in Person record
          }
        }
      }

      // No longer using in-memory storage, only Person records
    } catch (error) {
      console.error('Failed to check Zoom auth:', error);
    }
  };

  const handleConnect = () => {
    setIsLoading(true);

    const identity = userIdentityStore.currentIdentity;
    if (!identity) {
      console.error('No user identity available for Zoom OAuth');
      videoCreatorStore.toastState = {
        state: 'error',
        message: 'Unable to authenticate - no user identity found',
      };
      setIsLoading(false);
      return;
    }

    // Identity.email now always has a value (placeholder for Dato users)
    const userEmail = identity.email || '';

    // Get current URL params to preserve them
    const storyId = searchParams.get('storyId');
    const env = searchParams.get('env');

    // Create state parameter with consistent user identity info
    const state = Buffer.from(
      JSON.stringify({
        // Always use the current identity from UserIdentityStore
        userId: identity.id,
        userEmail: userEmail, // Now includes placeholder emails for Dato users
        userSource: identity.source, // 'clerk' or 'dato'
        // Organization info
        organizationId:
          videoCreatorStore.organization?.id ||
          datoPerson.selectedOrganization?.id ||
          datoPerson.getFallbackOrganization()?.id,
        // Person ID if it exists
        personId: datoPerson.person?.id,
        // URL params to restore
        storyId: storyId,
        env: env,
        timestamp: Date.now(),
      }),
    ).toString('base64');

    // Build OAuth URL
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: process.env.REACT_APP_ZOOM_CLIENT_ID || '',
      redirect_uri: `${process.env.REACT_APP_API_URL}/api/zoom/oauth-callback`,
      state: state,
    });

    // Redirect to Zoom OAuth
    window.location.href = `https://zoom.us/oauth/authorize?${params.toString()}`;
  };

  const handleDisconnect = async () => {
    setIsLoading(true);
    try {
      // Clear zoom info from datoPerson if person record exists
      if (datoPerson?.person) {
        try {
          await datoPerson.updateZoomInfo(null);
        } catch (error) {
          console.error('Failed to clear zoom info from datoPerson:', error);
          // Continue even if this fails
        }
      }

      // Call API to disconnect and clear Person record
      const apiUrl = process.env.REACT_APP_API_URL || '';
      const identity = userIdentityStore.currentIdentity;
      const response = await fetch(`${apiUrl}/api/zoom/disconnect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: identity?.email || datoPerson?.person?.email,
          personId: datoPerson?.person?.id,
        }),
      });

      if (response.ok) {
        setIsAuthenticated(false);
        setZoomEmail(null);
      }
    } catch (error) {
      console.error('Failed to disconnect Zoom:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Don't render anything if already authenticated
  // The parent component will show the View Recordings button instead
  if (isAuthenticated) {
    return null;
  }

  return (
    <ConnectButton onClick={handleConnect} disabled={isLoading}>
      {isLoading ? (
        'Connecting...'
      ) : (
        <ButtonContent>
          Connect
          <img src={zoomLogoText} alt="Zoom" />
          Account
        </ButtonContent>
      )}
    </ConnectButton>
  );
};

const ConnectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  background: transparent;
  color: #848484;
  border: 1px solid #848484;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  line-height: 140%;
  cursor: pointer;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: rgba(69, 212, 131, 0.1);
    border-color: #45d483;
    color: #45d483;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonContent = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
`;

export default ZoomAuthButton;
